// WeekdayRevenueECharts3D.jsx
import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import 'echarts-gl';
import { parseISO, subDays, addDays, format } from 'date-fns';

// 0 = Sunday … 6 = Saturday
const WEEKDAYS = [
    'Sunday','Monday','Tuesday',
    'Wednesday','Thursday','Friday','Saturday'
];
const WEEKDAY_COLORS = [
    '#1f77b4','#ff7f0e','#2ca02c',
    '#d62728','#9467bd','#8c564b','#e377c2'
];

/**
 * Build a complete list of week‑starts (Sundays) covering your data range,
 * then zero‑fill and aggregate revenues into { [week]: { Sunday:…, … } }.
 */
function getWeekBuckets(transactions) {
    if (!transactions.length) return { weeks: [], buckets: {} };

    const dates = transactions.map(t => parseISO(t.date));
    const minD  = new Date(Math.min(...dates.map(d=>d.getTime())));
    const maxD  = new Date(Math.max(...dates.map(d=>d.getTime())));

    // first Sunday on or before minD
    let firstSunday = subDays(minD, minD.getDay());
    // last Sunday on or before maxD, then push one more if maxD isn't itself Sunday
    const lastSunday = subDays(maxD, maxD.getDay());
    // build weeks array: every Sunday from first → last
    const weeks = [];
    for (let wk = firstSunday; wk <= lastSunday; wk = addDays(wk, 7)) {
        weeks.push(format(wk, 'yyyy-MM-dd'));
    }

    // initialize buckets with zeros
    const buckets = Object.fromEntries(
        weeks.map(wk => [
            wk,
            Object.fromEntries(WEEKDAYS.map(day => [day, 0]))
        ])
    );

    // fill in revenues
    transactions.forEach(({ date, revenue }) => {
        const d      = parseISO(date);
        const sunday = format(subDays(d, d.getDay()), 'yyyy-MM-dd');
        const day    = WEEKDAYS[d.getDay()];
        if (buckets[sunday]) {
            buckets[sunday][day] += revenue;
        }
    });

    return { weeks, buckets };
}
function dedupeByDate(rows, strategy = 'last') {
    const seen = new Map(); // key -> value we keep

    // If keeping the LAST value, iterate forward and overwrite.
    // If you want FIRST, iterate forward and only set if not exists.
    // I'll implement 'last' here:
    for (const { date, revenue } of rows) {
        const keyDate = format(parseISO(String(date).trim()), 'yyyy-MM-dd');
        const val = Number(revenue) || 0;

        if (!seen.has(keyDate)) {
            seen.set(keyDate, val);
        } else {
            switch (strategy) {
                case 'first':
                    // keep existing
                    break;
                case 'max':
                    seen.set(keyDate, Math.max(seen.get(keyDate), val));
                    break;
                case 'nonzero':
                    if (seen.get(keyDate) === 0 && val !== 0) seen.set(keyDate, val);
                    break;
                case 'last':
                default:
                    seen.set(keyDate, val);
            }
        }
    }

    return [...seen.entries()].map(([date, revenue]) => ({ date, revenue }));
}
export default function WeekdayRevenueECharts3D({ transactions = [] }) {
    // 1) Compute complete weeks & buckets

    // console.log(transactions);
    const deduped = useMemo(() => dedupeByDate(transactions, 'last'), [transactions]);
    const { weeks, buckets } = useMemo(() => getWeekBuckets(deduped), [deduped]);

    // 2) Build [xIndex, yIndex, zValue] data
    const data3D = useMemo(() => {
        return weeks.flatMap((wk, xi) =>
            WEEKDAYS.map((wd, yi) => [ xi, yi, buckets[wk][wd] ])
        );
    }, [weeks, buckets]);

    // 3) Chart option
    const option = useMemo(() => ({
       tooltip: {
             trigger: 'item',                // make sure we’re listening per‑bar
             triggerOn: 'mousemove|click',   // listen to both hover and tap
             formatter: params => {
               const [xi, yi, z] = params.value;
               const sundayDate = parseISO(weeks[xi]);
              const realDate   = addDays(sundayDate, yi);
              return `${format(realDate,'yyyy-MM-dd')} — ${WEEKDAYS[yi]}: ₪${z}`;
             }
      },
        xAxis3D: {
            type: 'category',
            name: '',
            data:      weeks,
            axisLabel: { show: false },
            // show: false,
        },
        yAxis3D: {
            type: 'category',
            data: WEEKDAYS,
            name: ''
        },
        zAxis3D: {
            type: 'value',
            name: '₪ Revenue'
        },
        grid3D: {
            viewControl: { projection: 'orthographic' }
        },
        series: [{
            type: 'bar3D',
            data: data3D,
            shading: 'lambert',
            barSize: 3.5,                   // thicker bars
            barGap: '-100%',                // no gap between bars
            emphasis: { focus: 'series', label: { show: true } },
            itemStyle: {
                color: params => WEEKDAY_COLORS[params.data[1]]
            }
        }]
    }), [weeks, data3D]);

    // 4) Render chart
    return (
        <ReactECharts
            option={option}
            style={{ height: 400, width: '100%' }}
            notMerge
            lazyUpdate
        />
    );
}
